import { Component, Inject, OnInit, DOCUMENT } from '@angular/core'
import { TranslateService, TranslatePipe } from '@ngx-translate/core'
import { setLanguage } from '../../shared/shared.module'
import { AuthService } from '@services/auth.service'
import { CookieService } from '@services/cookie.service'
import { interval, Observable } from 'rxjs'
import { filter, startWith, switchMap } from 'rxjs/operators'
import { AlertService } from '@services/alert.service'
import { AlertsResponse } from '@models/alerts/alerts-response'
import { ServiceNotification } from '@models/service-notification'
import { NotificationService } from '@services/notification.service'
import { FocusRemoverDirective } from '../../directives/focus-remover.directive'
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router'
import { NgClass, AsyncPipe } from '@angular/common'
import { NavLoginComponent } from '../../components/nav-login/nav-login.component'
import { AlertComponent } from 'ngx-bootstrap/alert'
import { CookieNoticeComponent } from '../../components/cookie-notice/cookie-notice.component'

/**
 * @ignore
 */
@Component({
  selector: 'app-dashboard',
  templateUrl: './default-layout.component.html',
  styleUrls: ['./default-layout.component.scss'],
  imports: [
    FocusRemoverDirective,
    RouterLink,
    RouterLinkActive,
    NgClass,
    NavLoginComponent,
    AlertComponent,
    RouterOutlet,
    CookieNoticeComponent,
    AsyncPipe,
    TranslatePipe
  ]
})
export class DefaultLayoutComponent implements OnInit {
  isNavbarCollapsed = true
  languages = new Map()
  alerts: AlertsResponse
  serviceNotifications$: Observable<ServiceNotification[]> = this.notificationService.notifications$

  // Check if the site has been embedded and needs to be blocked due to suspicious activity.
  // window !== window.top : true => The site is in a frame.
  embedded: boolean = window !== window.top

  links = {
    termsOfUsage: {
      fi: {
        link: 'https://www.oph.fi/fi/node/17792'
      },
      en: {
        link: 'https://www.oph.fi/en/node/17792'
      },
      sv: {
        link: 'https://www.oph.fi/sv/node/17792'
      }
    },
    privacyPolicy: {
      fi: {
        link: 'https://www.oph.fi/fi/node/17791'
      },
      en: {
        link: 'https://www.oph.fi/en/node/17791'
      },
      sv: {
        link: 'https://www.oph.fi/sv/node/17791'
      }
    },
    accessibilityStatement: {
      fi: {
        link: 'https://www.oph.fi/fi/node/17793'
      },
      en: {
        link: 'https://www.oph.fi/en/node/17793'
      },
      sv: {
        link: 'https://www.oph.fi/sv/node/17793'
      }
    }
  }

  logos = {
    oph: {
      fi: {
        src: 'assets/img/logos/OPH_Su_Ru_vaaka_RGB.png',
        alt: 'Opetushallitus'
      },
      en: {
        src: 'assets/img/logos/OPH_En_vaaka_rgb.png',
        alt: 'Finnish National Agency for Education'
      },
      sv: {
        src: 'assets/img/logos/OPH_Su_Ru_vaaka_RGB.png',
        alt: 'Utbildningsstyrelsen'
      }
    }
  }

  brand = {
    fi: {
      img: 'assets/img/aoe_logo_fi_45.png',
      alt: 'Avointen oppimateriaalien kirjasto'
    },
    en: {
      img: 'assets/img/aoe_logo_en_45.png',
      alt: 'Library of Open Educational Resources'
    },
    sv: {
      img: 'assets/img/aoe_logo_sv_45.png',
      alt: 'Biblioteket för öppna lärresurser'
    }
  }

  showNotice = true

  private navFocusObserver?: MutationObserver

  constructor(
    public translate: TranslateService,
    public authService: AuthService,
    private cookieSvc: CookieService,
    private alertSvc: AlertService,
    private notificationService: NotificationService,
    private router: Router,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.showNotice = !this.cookieSvc.isCookiePolicyAccepted()

    this.languages.set('fi', {
      label: 'FI',
      srText: 'Suomi: Vaihda kieli suomeksi'
    })

    this.languages.set('en', {
      label: 'EN',
      srText: 'English: Change language to English'
    })

    this.languages.set('sv', {
      label: 'SV',
      srText: 'Svenska: Byt språk till svenska'
    })
  }

  ngOnInit(): void {
    this.notificationService.getActiveNotifications()

    interval(5 * 60 * 1000) // minutes x seconds x milliseconds
      .pipe(
        startWith(0),
        switchMap(() => this.alertSvc.updateAlerts())
      )
      .subscribe((response: AlertsResponse) => (this.alerts = response))

    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        const isInitialPageLoad = event.id === 1
        if (!isInitialPageLoad) {
          this.moveFocusToHeading()
        }
      })
  }

  private moveFocusToHeading(): void {
    const main = this.document.getElementById('main-content')
    if (!main) {
      return
    }
    main.focus()
    this.navFocusObserver?.disconnect()

    const focusHeading = (): boolean => {
      const active = this.document.activeElement
      if (active && active !== main && active !== this.document.body) {
        return true
      }
      const heading = main.querySelector('h1')
      if (heading) {
        heading.setAttribute('tabindex', '-1')
        heading.focus()
        return true
      }
      return false
    }

    this.navFocusObserver = new MutationObserver(() => {
      if (focusHeading()) {
        this.navFocusObserver?.disconnect()
      }
    })
    this.navFocusObserver.observe(main, { childList: true, subtree: true })
  }

  /**
   * Set language
   */
  changeLanguage(lang: string): void {
    setLanguage(lang)
    this.translate.use(lang)
  }

  hideCookieNotice(): void {
    this.showNotice = false
  }

  skipToMainContent(event: Event): void {
    event.preventDefault()
    const main = this.document.getElementById('main-content')
    if (main) {
      main.focus()
      main.scrollIntoView()
    }
  }
}
